import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";
import { TEST } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import HelpVideoButton from "@/components/HelpVideoButton";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Plus, Power, Mail, MailCheck, Pencil, Eye, Trash2, AlertTriangle, Search as SearchIcon, X, KeyRound } from "lucide-react";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n) || 0);

function IconBtn({ label, testId, onClick, disabled, children, className = "", variant = "outline" }) {
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            data-testid={testId}
            onClick={onClick}
            disabled={disabled}
            variant={variant}
            size="icon"
            aria-label={label}
            className={`rounded-full h-9 w-9 shrink-0 ${className}`}
          >
            {children}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function SubscriberHelpers() {
  const { t } = useT();
  const [helpers, setHelpers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", mobile: "", email: "" });
  const [saving, setSaving] = useState(false);

  // Search / filter / sort state
  const [query, setQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // "all" | "active" | "inactive"
  const [sortKey, setSortKey] = useState("newest"); // "newest" | "oldest" | "collection" | "registrations"

  // Edit dialog
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editBusy, setEditBusy] = useState(false);

  // View dialog
  const [viewing, setViewing] = useState(null); // {...helper profile}

  // Delete confirmation
  const [confirmDel, setConfirmDel] = useState(null); // {helper, pending}
  const [delBusy, setDelBusy] = useState(false);

  // Credentials (password / PIN) dialog
  const [credFor, setCredFor] = useState(null); // helper being managed
  const [credPw, setCredPw] = useState("");
  const [credBusy, setCredBusy] = useState(false);

  const resetHelperPassword = async () => {
    if (credPw.length < 6) { toast.error(t("msg.passwordShort")); return; }
    setCredBusy(true);
    try {
      await api.post(`/subscriber/helpers/${credFor.helper_id}/reset-password`, { new_password: credPw });
      toast.success(t("sec.passwordResetOk"));
      setCredPw("");
      setCredFor(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setCredBusy(false); }
  };

  const resetHelperPin = async () => {
    setCredBusy(true);
    try {
      await api.post(`/subscriber/helpers/${credFor.helper_id}/reset-pin`);
      toast.success(t("sec.pinResetOk"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setCredBusy(false); }
  };

  const load = () => api.get("/subscriber/helpers-with-email").then((r) => setHelpers(r.data));
  useEffect(() => { load(); }, []);

  // Multi-haat (Enterprise): let the owner pick which haat the helper belongs to
  const [haatMeta, setHaatMeta] = useState(null);
  useEffect(() => { api.get("/subscriber/haats/meta").then((r) => setHaatMeta(r.data)).catch(() => {}); }, []);
  const multiHaat = (haatMeta?.haats?.length || 0) > 1;

  const save = async () => {
    if (!form.name || !form.mobile) { toast.error("Name & mobile required"); return; }
    setSaving(true);
    try {
      await api.post("/subscriber/helpers", form);
      toast.success("Helper added");
      setOpen(false); setForm({ name: "", mobile: "", email: "", haat_id: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const toggle = async (id) => { try { await api.patch(`/subscriber/helpers/${id}/toggle`); load(); } catch { toast.error("Failed"); } };

  const resendInvite = async (h) => {
    if (!h.email) { toast.error("Add an email to this helper first"); return; }
    try {
      const r = await api.post(`/subscriber/helpers/${h.helper_id}/resend-invite`);
      toast.success(`Google invite queued to ${r.data.queued_to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); }
  };

  const openEdit = (h) => {
    setEditing(h);
    setEditForm({ name: h.name || "", mobile: h.mobile || "", role_label: h.role_label || "helper", is_active: !!h.is_active });
  };

  const submitEdit = async () => {
    if (!editing) return;
    if (!editForm.name || !editForm.mobile) { toast.error("Name and mobile are required"); return; }
    setEditBusy(true);
    try {
      await api.patch(`/subscriber/helpers/${editing.helper_id}`, editForm);
      toast.success("Helper updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setEditBusy(false); }
  };

  const openView = async (h) => {
    try {
      const r = await api.get(`/subscriber/helpers/${h.helper_id}/profile`);
      setViewing(r.data);
    } catch { toast.error("Could not load profile"); }
  };

  const askDelete = (h) => setConfirmDel({ helper: h, pending: h.animals_registered ? null : 0, force: false });

  const runDelete = async () => {
    if (!confirmDel) return;
    setDelBusy(true);
    try {
      const url = `/subscriber/helpers/${confirmDel.helper.helper_id}${confirmDel.force ? "?force=true" : ""}`;
      await api.delete(url);
      toast.success("Helper deleted");
      setConfirmDel(null);
      load();
    } catch (e) {
      // Backend returns a 400 with a helpful message when there's pending work.
      const detail = e?.response?.data?.detail || "Delete failed";
      // Extract the pending count from the message for a clearer warning UI.
      const m = detail.match(/(\d+) pending animal/);
      if (m) {
        setConfirmDel((c) => ({ ...c, pending: Number(m[1]), warning: detail }));
      } else {
        toast.error(detail);
        setConfirmDel(null);
      }
    } finally { setDelBusy(false); }
  };

  // Search / filter / sort applied client-side over the enriched list from /helpers-with-email.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = helpers.filter((h) => {
      if (filterStatus === "active" && !h.is_active) return false;
      if (filterStatus === "inactive" && h.is_active) return false;
      if (!q) return true;
      return (h.name || "").toLowerCase().includes(q) || (h.mobile || "").includes(q);
    });
    const collectionOf = (h) => (h.total_collection || 0);
    const regsOf = (h) => (h.animals_registered || 0) + (h.sellers_registered || 0) + (h.purchasers_registered || 0);
    const dateOf = (h) => new Date(h.created_at || 0).getTime();
    list.sort((a, b) => {
      if (sortKey === "newest") return dateOf(b) - dateOf(a);
      if (sortKey === "oldest") return dateOf(a) - dateOf(b);
      if (sortKey === "collection") return collectionOf(b) - collectionOf(a);
      if (sortKey === "registrations") return regsOf(b) - regsOf(a);
      return 0;
    });
    return list;
  }, [helpers, query, filterStatus, sortKey]);

  return (
    <DashboardShell
      title={t("nav.helpers")}
      action={
        <div className="flex gap-2 items-center">
          <HelpVideoButton screen="helper_management" />
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid={TEST.subAddHelper} className="am-btn-primary"><Plus className="w-4 h-4 mr-1.5" /> {t("sub.addHelper")}</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display">{t("sub.addHelper")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder={t("common.name")} className="am-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <Input placeholder={t("common.mobile")} inputMode="numeric" className="am-input" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "") })} />
              <Input data-testid="sub-add-helper-email" placeholder="Email (for Google sign-in, optional)" type="email" className="am-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              {multiHaat && (
                <Select value={form.haat_id || haatMeta.haats[0].haat_id} onValueChange={(v) => setForm({ ...form, haat_id: v })}>
                  <SelectTrigger className="am-input" data-testid="sub-add-helper-haat"><SelectValue placeholder={t("haat.assign")} /></SelectTrigger>
                  <SelectContent>
                    {haatMeta.haats.map((h) => <SelectItem key={h.haat_id} value={h.haat_id}>{h.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            <DialogFooter>
              <Button data-testid={TEST.subSaveHelper} disabled={saving} className="am-btn-primary w-full" onClick={save}>{saving ? "…" : t("common.save")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      }
    >
      {/* Search + Filter + Sort toolbar */}
      <div className="am-card p-3 mb-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1 min-w-[200px]">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="helpers-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or mobile"
            className="am-input pl-9 pr-9"
          />
          {query && (
            <button aria-label="Clear" onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-muted-foreground hover:bg-muted">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger data-testid="helpers-filter-status" className="am-input w-full sm:w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All helpers</SelectItem>
            <SelectItem value="active">Active only</SelectItem>
            <SelectItem value="inactive">Inactive only</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortKey} onValueChange={setSortKey}>
          <SelectTrigger data-testid="helpers-sort" className="am-input w-full sm:w-[210px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Sort · Newest first</SelectItem>
            <SelectItem value="oldest">Sort · Oldest first</SelectItem>
            <SelectItem value="collection">Sort · Highest collection</SelectItem>
            <SelectItem value="registrations">Sort · Most registrations</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="am-card p-2 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b">
              <th className="px-4 py-3">{t("common.name")}</th>
              <th className="px-4 py-3">{t("common.mobile")}</th>
              <th className="px-4 py-3 hidden md:table-cell">Registrations</th>
              <th className="px-4 py-3 hidden md:table-cell">Collection</th>
              <th className="px-4 py-3">{t("common.status")}</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((h) => (
              <tr key={h.helper_id} className="border-b last:border-0" data-testid={`helper-row-${h.helper_id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold">{h.name}</div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{h.role_label || "helper"}</div>
                  {h.email ? (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-primary" data-testid={`helper-email-linked-${h.helper_id}`}>
                      <MailCheck className="w-3 h-3" /> Google-ready
                    </div>
                  ) : (
                    <div className="mt-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">No email</div>
                  )}
                </td>
                <td className="px-4 py-3 font-mono">
                  {h.mobile}
                  {multiHaat && h.haat_name && (
                    <div className="mt-1"><span className="am-chip text-[10px]">{h.haat_name}</span></div>
                  )}
                </td>
                <td className="px-4 py-3 hidden md:table-cell font-mono text-xs">
                  <div>{h.animals_registered || 0} animals</div>
                  <div className="text-muted-foreground">{h.sellers_registered || 0} sellers · {h.purchasers_registered || 0} purchasers</div>
                </td>
                <td className="px-4 py-3 hidden md:table-cell font-mono text-xs">
                  <div className="font-semibold text-foreground">{fmtINR(h.total_collection)}</div>
                  <div className="text-muted-foreground">today · {fmtINR(h.today_collection)}</div>
                </td>
                <td className="px-4 py-3"><span className={`am-chip ${h.is_active ? "" : "bg-muted text-muted-foreground"}`}>{h.is_active ? t("common.active") : t("common.disabled")}</span></td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1.5 flex-wrap">
                    {h.email && (
                      <IconBtn label={`Send Google invite to ${h.email}`} testId={`helper-resend-invite-${h.helper_id}`} onClick={() => resendInvite(h)}>
                        <Mail className="w-4 h-4" />
                      </IconBtn>
                    )}
                    <IconBtn label="View profile" testId={`helper-view-${h.helper_id}`} onClick={() => openView(h)}>
                      <Eye className="w-4 h-4" />
                    </IconBtn>
                    <IconBtn label="Edit helper" testId={`helper-edit-${h.helper_id}`} onClick={() => openEdit(h)}>
                      <Pencil className="w-4 h-4" />
                    </IconBtn>
                    <IconBtn label={t("sec.credentials")} testId={`helper-credentials-${h.helper_id}`} onClick={() => { setCredFor(h); setCredPw(""); }}>
                      <KeyRound className="w-4 h-4" />
                    </IconBtn>
                    <IconBtn label={h.is_active ? "Deactivate" : "Activate"} testId={TEST.subToggleHelper(h.helper_id)} onClick={() => toggle(h.helper_id)}>
                      <Power className="w-4 h-4" />
                    </IconBtn>
                    <IconBtn label="Delete helper" testId={`helper-delete-${h.helper_id}`} onClick={() => askDelete(h)} className="text-destructive hover:text-destructive">
                      <Trash2 className="w-4 h-4" />
                    </IconBtn>
                  </div>
                </td>
              </tr>
            ))}
            {visible.length === 0 && <tr><td colSpan={6} className="py-12 text-center text-muted-foreground">{helpers.length === 0 ? t("sub.noHelpers") : "No helpers match your filters."}</td></tr>}
          </tbody>
        </table>
      </div>

      {/* CREDENTIALS DIALOG — owner creates/resets helper password & PIN */}
      <Dialog open={!!credFor} onOpenChange={(v) => { if (!v) setCredFor(null); }}>
        <DialogContent className="max-w-sm" data-testid="helper-credentials-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-primary" /> {t("sec.credentials")}
            </DialogTitle>
            <DialogDescription>{credFor?.name} · +91 {credFor?.mobile}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-xs font-semibold text-muted-foreground">{t("sec.setNewPassword")}</label>
              <div className="flex gap-2">
                <Input
                  data-testid="helper-cred-password"
                  type="text"
                  placeholder={t("auth.newPasswordHint")}
                  className="am-input flex-1"
                  value={credPw}
                  onChange={(e) => setCredPw(e.target.value)}
                />
                <Button data-testid="helper-cred-password-save" className="am-btn-primary shrink-0" disabled={credBusy || credPw.length < 6} onClick={resetHelperPassword}>
                  {credBusy ? "…" : t("common.save")}
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">{t("sec.passwordShareHint")}</p>
            </div>
            <div className="rounded-2xl border p-3 flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold">{t("sec.resetPin")}</div>
                <div className="text-xs text-muted-foreground">{t("sec.resetPinHint")}</div>
              </div>
              <Button data-testid="helper-cred-reset-pin" variant="outline" className="rounded-full shrink-0" disabled={credBusy} onClick={resetHelperPin}>
                {t("sec.resetPin")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* EDIT DIALOG */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Edit helper</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Name</label>
                <Input data-testid="helper-edit-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="am-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Mobile</label>
                <Input data-testid="helper-edit-mobile" inputMode="numeric" value={editForm.mobile} onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value.replace(/\D/g, "") })} className="am-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Role</label>
                <Select value={editForm.role_label} onValueChange={(v) => setEditForm({ ...editForm, role_label: v })}>
                  <SelectTrigger data-testid="helper-edit-role" className="am-input"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="helper">Helper</SelectItem>
                    <SelectItem value="cashier">Cashier</SelectItem>
                    <SelectItem value="gate-keeper">Gate-keeper</SelectItem>
                    <SelectItem value="registration">Registration desk</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between rounded-2xl border p-3">
                <div>
                  <div className="text-sm font-semibold">Status</div>
                  <div className="text-xs text-muted-foreground">Inactive helpers cannot sign in.</div>
                </div>
                <Switch data-testid="helper-edit-status" checked={!!editForm.is_active} onCheckedChange={(v) => setEditForm({ ...editForm, is_active: v })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setEditing(null)}>{t("common.cancel")}</Button>
            <Button data-testid="helper-edit-save" disabled={editBusy} onClick={submitEdit} className="am-btn-primary">{editBusy ? "…" : t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* VIEW / PROFILE DIALOG */}
      <Dialog open={!!viewing} onOpenChange={(v) => { if (!v) setViewing(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">{viewing?.name}</DialogTitle>
            <DialogDescription className="capitalize">{viewing?.role_label || "helper"} · {viewing?.mobile}</DialogDescription>
          </DialogHeader>
          {viewing && (
            <div className="space-y-3" data-testid="helper-view-panel">
              <div className="grid grid-cols-2 gap-3">
                <StatCard label="Registered on" value={new Date(viewing.created_at).toLocaleDateString()} />
                <StatCard label="Last login" value={viewing.last_login_at ? new Date(viewing.last_login_at).toLocaleString() : "—"} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <StatCard label="Animals" value={viewing.animals_registered ?? 0} />
                <StatCard label="Sellers" value={viewing.sellers_registered ?? 0} />
                <StatCard label="Purchasers" value={viewing.purchasers_registered ?? 0} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <StatCard label="Today's collection" value={fmtINR(viewing.today_collection)} highlight />
                <StatCard label="Overall collection" value={fmtINR(viewing.total_collection)} highlight />
              </div>
              <StatCard
                label="Overall performance"
                value={
                  `${(viewing.animals_registered ?? 0) + (viewing.sellers_registered ?? 0) + (viewing.purchasers_registered ?? 0)} total registrations · ${viewing.receipts_issued ?? 0} receipts`
                }
              />
              {viewing.pending_work > 0 && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs px-3 py-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {viewing.pending_work} animal(s) currently in the haat under this helper.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setViewing(null)}>{t("common.close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DELETE CONFIRMATION */}
      <Dialog open={!!confirmDel} onOpenChange={(v) => { if (!v) setConfirmDel(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" /> Delete helper?
            </DialogTitle>
            <DialogDescription className="pt-2 leading-relaxed">
              {confirmDel?.warning
                ? confirmDel.warning
                : `Delete ${confirmDel?.helper?.name}? They will no longer be able to sign in or register on this haat.`}
            </DialogDescription>
          </DialogHeader>
          {confirmDel?.pending > 0 && (
            <label className="flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
              <input type="checkbox" data-testid="helper-delete-force" checked={!!confirmDel.force} onChange={(e) => setConfirmDel({ ...confirmDel, force: e.target.checked })} />
              I understand — force delete despite pending work
            </label>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setConfirmDel(null)}>{t("common.cancel")}</Button>
            <Button
              data-testid="helper-delete-confirm"
              disabled={delBusy || (confirmDel?.pending > 0 && !confirmDel.force)}
              onClick={runDelete}
              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {delBusy ? "…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}

function StatCard({ label, value, highlight = false }) {
  return (
    <div className={`rounded-2xl border p-3 ${highlight ? "bg-primary/5 border-primary/20" : ""}`}>
      <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-sm font-semibold mt-1 break-words">{value}</div>
    </div>
  );
}
